// ============================================================
//  CUSTOMERS PAGE — ADMIN DASHBOARD
//  Path: src/pages/CustomersPage.tsx
//
//  REWRITTEN: this file had been accidentally overwritten with a copy
//  of CompaniesPage.tsx, so the Customers menu showed the companies
//  list. Rebuilt against the backend's existing endpoints:
//
//    GET    /api/admin/users?role=customer&search=&isActive=&page=&limit=
//           (all admin roles; search matches first/last name, email, phone)
//    PATCH  /api/admin/users/:id/active   — super_admin, admin
//    DELETE /api/admin/users/:id          — super_admin only, needs a
//           sign-in within the last 5 minutes (requireRecentAuth) — goes
//           through useRecentAuth so the admin is asked to re-verify first
//
//  Clicking a row opens CustomerDetailPage (/customers/:id, keyed by the
//  User id — the same id getUserById there expects).
//
//  CHANGE (2026-10-10): new "Support" column — help requests from this
//  customer's app that nobody has answered yet (red number, links to Support
//  filtered to that person). One request per page of results; a failure
//  only leaves the column empty. Same as ExpertsPage.
//
//  Access:
//    super_admin → view, activate/deactivate, delete
//    admin       → view, activate/deactivate
//    customer_service → view only
// ============================================================

import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { listUsers, toggleUserActive, deleteUser } from '../services/admin.service'
import { useAuth } from '../context/AuthContext'
import { useRecentAuth } from '../hooks/useRecentAuth'
import { getSupportOpenSummaryApi, SupportOpenSummary } from '../api/support.api'

interface UserIssue {
  code:     string
  severity: 'blocking' | 'warning'
  message:  string
}

// Computed server-side (admin.service.js's buildCustomerNeedsAction) —
// deliberately separate from UserIssue: nothing is wrong, an admin
// decision is just outstanding. Always [] today (no customer approval
// workflow exists in the schema), kept for parity with ExpertsPage so
// both list pages share the same column.
interface NeedsActionItem {
  code:    string
  message: string
}

interface CustomerRow {
  id:        string
  firstName: string | null
  lastName:  string | null
  email:     string | null
  phone:     string | null
  isActive:  boolean
  createdAt: string
  roles:     string[]
  customer: {
    id: string
    city: string | null
    homeAddressCity: string | null
    homeAddressProvince: string | null
    homeAddressCountry: string | null
  } | null
  // Computed server-side (see admin.service.js's buildCustomerIssues)
  // — deactivated account, a failed charge, an open dispute. Absent
  // on older cached responses, so always treat as possibly undefined.
  issues?: UserIssue[]
  // See NeedsActionItem above.
  needsAction?: NeedsActionItem[]
}

type StatusFilter = 'all' | 'true' | 'false'

const PAGE_SIZE = 20

const CustomersPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isSuperAdmin = !!user?.roles.includes('super_admin')
  const canModify    = isSuperAdmin || !!user?.roles.includes('admin')

  const [customers, setCustomers] = useState<CustomerRow[]>([])
  // Open help requests per user id, for the rows on this page.
  const [supportByUser, setSupportByUser] = useState<SupportOpenSummary['byUser']>({})
  const [total, setTotal]         = useState(0)
  const [page, setPage]           = useState(1)
  const [loading, setLoading]     = useState(true)
  const [loadError, setLoadError] = useState('')
  const [actionError, setActionError] = useState('')

  // searchInput updates on every keystroke; search (what's actually
  // sent) follows it 300ms after typing stops, so each letter doesn't
  // fire its own request.
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch]           = useState('')
  const [status, setStatus]           = useState<StatusFilter>('all')
  const [issuesOnly, setIssuesOnly]   = useState(false)

  const [togglingId, setTogglingId]   = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<CustomerRow | null>(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleting, setDeleting]       = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const { runWithRecentAuth, reauthModal } = useRecentAuth()

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const fetchCustomers = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true)
    setLoadError('')
    try {
      const res = await listUsers({
        role:  'customer',
        page,
        limit: PAGE_SIZE,
        ...(search ? { search } : {}),
        ...(status !== 'all' ? { isActive: status } : {}),
        ...(issuesOnly ? { issuesOnly: 'true' } : {}),
      })
      const rows: CustomerRow[] = res.data.data ?? []
      setCustomers(rows)
      setTotal(res.data.pagination?.total ?? 0)
      // Support column (see header).
      const userIds = rows.map((c) => c.id).filter(Boolean)
      if (userIds.length) {
        getSupportOpenSummaryApi(userIds)
          .then((r: any) => setSupportByUser(r?.data?.data?.byUser || {}))
          .catch(() => setSupportByUser({}))
      } else {
        setSupportByUser({})
      }
    } catch (err: any) {
      console.error('Failed to fetch customers:', err)
      setLoadError(err?.response?.data?.message || (err?.isNetworkError
        ? 'Could not reach the server — check your connection and try again.'
        : 'Could not load customers — try again.'))
    } finally {
      if (!opts?.silent) setLoading(false)
    }
  }, [page, search, status, issuesOnly])

  useEffect(() => { fetchCustomers() }, [fetchCustomers])

  const fullName = (c: CustomerRow) =>
    [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unnamed customer'

  const handleToggleActive = async (c: CustomerRow) => {
    if (!canModify) return
    const next = !c.isActive
    if (!next && !window.confirm(`Deactivate ${fullName(c)}? They will be signed out and can't use the app until reactivated.`)) return
    setActionError('')
    setTogglingId(c.id)
    try {
      await toggleUserActive(c.id, next)
      await fetchCustomers({ silent: true })
    } catch (err: any) {
      console.error('Failed to update customer status:', err)
      setActionError(err?.response?.data?.message || `Failed to ${next ? 'activate' : 'deactivate'} ${fullName(c)} — try again.`)
    } finally {
      setTogglingId(null)
    }
  }

  const openDelete = (c: CustomerRow) => {
    setDeleteTarget(c)
    setDeleteConfirmText('')
    setDeleteError('')
  }

  const closeDelete = () => {
    setDeleteTarget(null)
    setDeleteConfirmText('')
    setDeleteError('')
  }

  const handleDelete = () => {
    if (!isSuperAdmin || !deleteTarget || deleteConfirmText !== 'DELETE') return
    const target = deleteTarget
    setDeleteError('')
    runWithRecentAuth(async () => {
      setDeleting(true)
      try {
        await deleteUser(target.id)
        closeDelete()
        // Deleting the last row on a page leaves that page empty — step back.
        if (customers.length === 1 && page > 1) setPage((p) => p - 1)
        else await fetchCustomers({ silent: true })
      } catch (err: any) {
        console.error('Failed to delete customer:', err)
        setDeleteError(err?.response?.data?.message || 'Failed to delete this customer — try again.')
      } finally {
        setDeleting(false)
      }
    })
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Customers</h1>
          <p className="text-gray-400 text-sm mt-1">{total} total customer{total === 1 ? '' : 's'}</p>
        </div>
        {!canModify && (
          <span className="px-3 py-1.5 text-xs rounded-full bg-yellow-600/20 text-yellow-400">View Only</span>
        )}
      </div>

      {/* Search + status filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by name, email or phone"
          className="flex-1 bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none"
        />
        <div className="flex gap-2">
          {([['all', 'All'], ['true', 'Active'], ['false', 'Inactive']] as [StatusFilter, string][]).map(([value, label]) => (
            <button key={value}
              onClick={() => { setStatus(value); setPage(1) }}
              className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                status === value ? 'bg-brand-600 text-white' : 'bg-surface-card border border-border-light text-gray-400 hover:text-white'
              }`}>
              {label}
            </button>
          ))}
          <button
            onClick={() => { setIssuesOnly((v) => !v); setPage(1) }}
            title="Deactivated accounts, failed charges, or open disputes"
            className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
              issuesOnly ? 'bg-red-600 text-white' : 'bg-surface-card border border-border-light text-gray-400 hover:text-white'
            }`}>
            ⚠️ Has Issues
          </button>
        </div>
      </div>

      {actionError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {actionError}
          <button onClick={() => setActionError('')} className="ml-3 text-red-300 hover:text-white">✕</button>
        </div>
      )}

      {loadError ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center justify-between gap-3">
          <p className="text-red-400 text-sm">{loadError}</p>
          <button onClick={() => fetchCustomers()}
            className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
            Try again
          </button>
        </div>
      ) : (
        <div className="bg-surface-card border border-border-light rounded-2xl overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-light">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Name</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Contact</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Country</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Province</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">City</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Status</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Issues</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Needs Action</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Support</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Joined</th>
                {canModify && <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={canModify ? 11 : 10} className="text-center py-8 text-gray-400">Loading...</td></tr>
              ) : customers.length === 0 ? (
                <tr><td colSpan={canModify ? 11 : 10} className="text-center py-8 text-gray-400">
                  {search || status !== 'all' || issuesOnly ? 'No customers match these filters' : 'No customers yet'}
                </td></tr>
              ) : (
                customers.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/customers/${c.id}`)}
                    className="border-b border-border-light cursor-pointer hover:bg-surface-tertiary/50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-green-700 flex items-center justify-center text-white text-sm font-bold shrink-0">
                          {c.firstName?.[0]?.toUpperCase() || '?'}
                        </div>
                        <p className="text-sm font-medium text-white">{fullName(c)}</p>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-sm text-gray-300">{c.email || '—'}</p>
                      <p className="text-xs text-gray-500">{c.phone || ''}</p>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-300">{c.customer?.homeAddressCountry || '—'}</td>
                    <td className="px-5 py-4 text-sm text-gray-300">{c.customer?.homeAddressProvince || '—'}</td>
                    <td className="px-5 py-4 text-sm text-gray-300">{c.customer?.homeAddressCity || c.customer?.city || '—'}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        c.isActive ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'
                      }`}>{c.isActive ? 'Active' : 'Inactive'}</span>
                    </td>
                    <td className="px-5 py-4">
                      {c.issues && c.issues.length > 0 ? (
                        <span
                          title={c.issues.map((i) => i.message).join('\n')}
                          className={`px-2 py-1 text-xs rounded-full ${
                            c.issues.some((i) => i.severity === 'blocking') ? 'bg-red-600/20 text-red-400' : 'bg-yellow-600/20 text-yellow-400'
                          }`}>
                          ⚠️ {c.issues.length}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      {c.needsAction && c.needsAction.length > 0 ? (
                        <span
                          title={c.needsAction.map((a) => a.message).join('\n')}
                          className="px-2 py-1 text-xs rounded-full bg-brand-600/20 text-brand-400">
                          🔔 {c.needsAction.length}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                      {(supportByUser[c.id]?.customer || 0) > 0 ? (
                        <Link
                          to={`/support?search=${encodeURIComponent(c.id)}&appType=customer&status=open`}
                          title="Help requests from this customer that nobody has answered yet"
                          className="px-2 py-1 text-xs font-semibold rounded-full bg-red-500/20 text-red-300 hover:bg-red-500/30">
                          💬 {supportByUser[c.id].customer}
                        </Link>
                      ) : (
                        <span className="text-xs text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-400">{new Date(c.createdAt).toLocaleDateString()}</td>
                    {canModify && (
                      <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-2">
                          <button onClick={() => handleToggleActive(c)} disabled={togglingId === c.id}
                            className={`px-3 py-1 text-xs rounded-lg disabled:opacity-50 transition-colors ${
                              c.isActive
                                ? 'bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20'
                                : 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                            }`}>
                            {togglingId === c.id ? 'Saving...' : c.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          {isSuperAdmin && (
                            <button onClick={() => openDelete(c)}
                              className="px-3 py-1 text-xs bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors">
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && !loadError && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-gray-400">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light">Previous</button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light">Next</button>
          </div>
        </div>
      )}

      {/* Delete — permanent, super_admin only. Typing DELETE is required
          because this removes the account and all of its data. */}
      {deleteTarget && isSuperAdmin && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={closeDelete}>
          <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-2">Delete Customer</h3>
            <p className="text-sm text-gray-400 mb-3">
              Permanently delete <span className="text-white">{fullName(deleteTarget)}</span> and all of their data.
              This cannot be undone. To only block access, use Deactivate instead.
            </p>
            {deleteError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-3">
                <p className="text-red-400 text-sm">{deleteError}</p>
              </div>
            )}
            <label className="block text-xs text-gray-500 mb-1">Type DELETE to confirm</label>
            <input type="text" value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} autoFocus
              className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white focus:border-red-500 focus:outline-none mb-4" />
            <div className="flex gap-3 justify-end">
              <button onClick={closeDelete}
                className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
              <button onClick={handleDelete} disabled={deleteConfirmText !== 'DELETE' || deleting}
                className="px-4 py-2 text-sm bg-red-700 text-white rounded-xl hover:bg-red-600 disabled:opacity-50 transition-colors">
                {deleting ? 'Deleting...' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {reauthModal}
    </div>
  )
}

export default CustomersPage